// Explore.jsx - Game exploration page with search and detailed game display
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { authService } from "../../services/auth";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { useBulkOnlineAvailability } from "../../hooks/useBulkOnlineAvailability";
import { useLetsPlayModal } from "../../hooks/useLetsPlayModal";
import { usePlayList } from "../../hooks/usePlayList";
import GameSearchDropdown from "../common/GameSearchDropdown";
import GamePreviewModal from "./GamePreviewModal";
import AddGameModal from "./AddGameModal";
import LetsPlayModal from "./LetsPlayModal";
import CurateSimplified from "./CurateSimplified";
import Marketplace from "./Marketplace";
import SimplifyReminderModal from "./SimplifyReminderModal";
import PlayListSidebar from "./PlayListSidebar";
import { GameTile } from "./CurateGameTile";
// import InsightCarousel from "./InsightCarousel";
import { readLastExploreGame, writeLastExploreGame } from "../../utils/exploreLastGameStorage";
import { ChevronRight, List, Smartphone, Search } from "lucide-react";
import { Capacitor } from '@capacitor/core';
import { CLOSE_ICON, JET_SET_GO_ICON, LIBRARY_ICON } from "../../constants/icons";
import { isAndroid, isInstalled } from '../../utils/pwaInstall';
import { installPrompts } from '../../config/installPrompts';
import { isFirefox } from '../../utils/browserDetection';
import ShareWarningBanner from '../common/ShareWarningBanner';
import '../../styles/share-warning.css';
import logger from '../../utils/logger';

const DEFAULT_SAMPLE_GAME_ID = 235375;

export function ExploreLandingCard({
  icon,
  title,
  description,
  offerPills,
  pills,
  ctaLabel,
  ctaTone = "secondary",
  disabled,
  onCardClick,
  ariaLabel,
}) {
  const cardClass = [
    "explore-landing-card",
    disabled ? "explore-landing-card--disabled" : "explore-landing-card--green",
  ].join(" ");

  const handleClick = () => { if (!disabled) onCardClick(); };
  const handleKey = (e) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      onCardClick();
    }
  };

  return (
    <div
      className={cardClass}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      title={disabled ? "Create a free account to import and curate your collection" : undefined}
      onClick={handleClick}
      onKeyDown={handleKey}
    >
      <div className="explore-landing-card__body">
        <div className="explore-landing-card__header">
          <span className="explore-landing-card__icon" aria-hidden="true">
            {icon}
          </span>
          <h3 className="explore-landing-card__title">{title}</h3>
        </div>
        {description && <p className="explore-landing-card__description">{description}</p>}
        {offerPills?.length > 0 && (
          <div className="explore-landing-card__offer">
            <div className="explore-landing-card__tags">
              {offerPills.map((label) => (
                <span key={label} className="explore-landing-card__pill explore-landing-card__pill--muted">
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
        {pills?.length > 0 && (
          <div className="explore-landing-card__account-tags">
            {pills.map((label) => (
              <span key={label} className="explore-landing-card__pill explore-landing-card__pill--account">
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={`explore-landing-card__cta explore-landing-card__cta--${ctaTone}`} aria-hidden="true">
        <span className="explore-landing-card__cta-label">
          {disabled ? "Sign up free. Quick Start in 2 minutes 🔒" : ctaLabel}
        </span>
        {!disabled && <ChevronRight className="explore-landing-card__cta-chevron" size={18} strokeWidth={2.5} />}
      </div>
    </div>
  );
}

// Simple debounce implementation
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

function Explore({ user, onOpenProfileForBgg: _onOpenProfileForBgg }) {
  const { trackClick } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams();
  const lastHandledExploreKey = useRef(null);
  const fetchedGameIdParam = useRef(null);
  // Core state - minimal and focused
  const [selectedGame, setSelectedGame] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCurateSidebar, setShowCurateSidebar] = useState(false);
  const [curateMode, setCurateMode] = useState(null);
  const [baseGame, setBaseGame] = useState(null);
  const latestSearchQueryRef = useRef(searchQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);


  // Modal states
  const [previewGame, setPreviewGame] = useState(null);
  const [addGameModal, setAddGameModal] = useState(null);
  const [marketplaceGame, setMarketplaceGame] = useState(null);
  const [reminderGame, setReminderGame] = useState(null);
  const [userMap] = useState(new Map());

  // Let's Play modal
  const {
    showModal: showLetSPlayModal,
    selectedGame: letSPlayGame,
    showSessionForm: showSessionFormInLetSPlay,
    showRulesSummary: showRulesSummaryInLetSPlay,
    setShowSessionForm,
    handlers: letSPlayHandlers,
  } = useLetsPlayModal(user, () => {});

  // Play List state
  const {
    playList,
    count: playListCount,
    loading: playListLoading,
    addToPlayList,
    removeFromPlayList,
    clearPlayList,
    isInPlayList,
  } = usePlayList(user);
  const [showPlayListSidebar, setShowPlayListSidebar] = useState(false);
  const [playListBanner, setPlayListBanner] = useState({ show: false, message: null });

  const showPlayListBanner = useCallback((message) => {
    setPlayListBanner({ show: true, message });
    setTimeout(() => setPlayListBanner({ show: false, message: null }), 3000);
  }, []);

  const handleTogglePlayList = useCallback(async (game) => {
    const gameId = game.game_id || game.id;
    if (isInPlayList(gameId)) {
      await removeFromPlayList(gameId);
    } else {
      await addToPlayList(game);
      showPlayListBanner(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'inherit', color: 'white' }}>
          Added to Play List <List size={16} />
        </span>
      );
    }
  }, [isInPlayList, addToPlayList, removeFromPlayList, showPlayListBanner]);

  // ScorePad state for Explore
  const [showScorePadInLetSPlay, setShowScorePadInLetSPlay] = useState(false);
  const [scorePadSessionData, setScorePadSessionData] = useState(null);

  // ScorePad handlers
  const handleOpenScorePad = useCallback(() => {
    setShowScorePadInLetSPlay(true);
  }, []);

  const handleCloseScorePad = useCallback(() => {
    setShowScorePadInLetSPlay(false);
  }, []);

  const handleLogSessionFromScorePad = useCallback((data) => {
    setScorePadSessionData(data);
    setShowScorePadInLetSPlay(false);
    setShowSessionForm(true);
  }, [setShowSessionForm]);

  // Collection state for GameTile ownership badge
  const [collection, setCollection] = useState([]);

  // Latest user activity for the hero greeting
  const [lastActivity, setLastActivity] = useState(null);

  // Refs
  const curateRef = useRef(null);

  // Hooks
  const gameIds = useMemo(() => {
    if (!selectedGame) return [];
    const gameId = selectedGame.game_id || selectedGame.id;
    return gameId ? [gameId] : [];
  }, [selectedGame]);
  const { getGameAvailability } = useBulkOnlineAvailability(gameIds);

  // Game action handlers
  const handleAddToMarketplace = useCallback((game) => {
    if (!game) return;
    logger.debug("[Explore] Opening marketplace for game:", { gameId: game.game_id || game.id, gameName: game.name });
    setMarketplaceGame(game);
  }, []);

  const handleAddReminder = useCallback(async (game) => {
    if (!user || !game) return;

    const gameId = game.game_id || game.id;
    logger.debug("[Explore] Adding reminder for game:", { gameId, gameName: game.name });

    try {
      const res = await httpRequest(`${API_BASE}/profile/reminders`, {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          game_id: gameId,
        }),
      });

      if (res.ok) {
        logger.debug("[Explore] Reminder added successfully");
      } else {
        logger.error("[Explore] Failed to add reminder:", res.status);
      }
    } catch (err) {
      logger.error("[Explore] Error adding reminder:", err);
    }
  }, [user]);

  const handleLetSPlay = useCallback((game) => {
    if (!game) return;
    if (!user) {
      navigate("/login");
      return;
    }
    letSPlayHandlers.openModal(game);
  }, [user, navigate, letSPlayHandlers]);

  const handleSearchSimilar = useCallback((game) => {
    if (!game) return;
    trackClick('similarity_check', { game_id: game.id || game.game_id, game_name: game.name, source: 'explore' });
    const gameId = game.id ?? game.game_id;
    navigate(`/curate?gameId=${gameId}&weight=0.5&scope=all`);
  }, [trackClick, navigate]);

  const handleCompare = useCallback((game) => {
    if (!selectedGame || !game) return;
    setBaseGame(selectedGame);
    setCurateMode('COMPARISON');
    setShowCurateSidebar(true);
  }, [selectedGame]);

  // Preload tile: logged-in prefers last saved pick, then falls back to Spirits of the Forest
  // Skip when a specific game is arriving via navigation state or URL param — those effects
  // handle selection themselves and must not be overwritten by this async fallback.
  useEffect(() => {
    if (location.state?.exploreGame || gameId || lastHandledExploreKey.current) return;

    let cancelled = false;
    const loadPreload = async () => {
      try {
        if (user?.id) {
          const saved = await readLastExploreGame(user.id);
          if (!cancelled && saved && ((saved.id !== null && saved.id !== undefined) || (saved.game_id !== null && saved.game_id !== undefined))) {
            const gid = saved.id ?? saved.game_id;
            setSelectedGame({ ...saved, id: gid, game_id: gid });
            return;
          }
        }

        const res = await httpRequest(`${API_BASE}/games/${DEFAULT_SAMPLE_GAME_ID}/details`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });
        if (!cancelled && res.ok) {
          const g = await res.json();
          const gid = g.id ?? g.game_id ?? DEFAULT_SAMPLE_GAME_ID;
          setSelectedGame({ ...g, id: gid, game_id: gid });
        }
      } catch (err) {
        if (err?.status === 404) {
          logger.warn("[Explore] sample game not found, skipping preload:", err?.message || err);
        } else {
          logger.error("[Explore] preload failed:", err);
        }
      }
    };
    loadPreload();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch user collection for ownership status
  useEffect(() => {
    const fetchCollection = async () => {
      if (!user) return;
      try {
        const res = await httpRequest(`${API_BASE}/profile/collection`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          const games = Array.isArray(data) ? data : (data.games || []);
          setCollection(games.map(g => ({ ...g, id: g.id || g.game_id })));
        }
      } catch (err) {
        logger.error("Failed to fetch collection:", err);
        setCollection([]);
      }
    };
    fetchCollection();
  }, [user]);

  // Fetch the latest game the user interacted with for the hero greeting
  useEffect(() => {
    const fetchLatestActivity = async () => {
      if (!user) return;
      try {
        const res = await httpRequest(`${API_BASE}/analytics/latest-activity`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.game_name) {
            setLastActivity(data);
            return;
          }
        }
      } catch (err) {
        logger.error("Failed to fetch latest activity:", err);
      }
      // Fallback: use locally stored last explore game
      const saved = await readLastExploreGame(user.id);
      if (saved) {
        setLastActivity({ game_id: saved.id ?? saved.game_id, game_name: saved.name ?? saved.game_name });
      }
    };
    fetchLatestActivity();
  }, [user]);

  // Game ownership status checker — returns status string for badge display
  const getGameOwnershipStatus = useCallback((gameId) => {
    if (!Array.isArray(collection) || !gameId) return false;
    const target = String(gameId);
    const found = collection.find(game =>
      String(game.game_id) === target || String(game.id) === target
    );
    if (!found) return false;
    const status = (found.ownership_status || '').toLowerCase();
    if (status === 'owned') return 'owned';
    if (status === 'interested') return 'interested';
    if (status === 'excluded') return 'excluded';
    if (status === 'no_shelf') return 'no_shelf';
    return 'no_shelf';
  }, [collection]);

  // Add to collection handler — opens AddGameModal for status selection and feedback
  const handleAddToCollection = useCallback(() => {
    if (!selectedGame) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setAddGameModal(selectedGame);
  }, [user, selectedGame, navigate]);

  const handleLaunchSessionFromAddGame = useCallback((savedGame) => {
    if (!savedGame) return;
    letSPlayHandlers.openModal(savedGame);
    letSPlayHandlers.logSession();
  }, [letSPlayHandlers]);

  // Refresh collection after AddGameModal saves
  const handleAddGameModalSuccess = useCallback(async (savedGame) => {
    setAddGameModal(null);
    if (!user) return;

    // Optimistically update the tile's ownership status before the server round-trip
    if (savedGame) {
      const gameId = String(savedGame.game_id || savedGame.id);
      setCollection((prev) => {
        const existingIndex = prev.findIndex(
          (g) => String(g.game_id) === gameId || String(g.id) === gameId
        );
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = { ...prev[existingIndex], ...savedGame };
          return updated;
        }
        return [...prev, savedGame];
      });
    }

    try {
      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const games = Array.isArray(data) ? data : (data.games || []);
        setCollection(games.map(g => ({ ...g, id: g.id || g.game_id })));
      }
    } catch (err) {
      logger.error("[Explore] Failed to refresh collection:", err);
    }
  }, [user]);

  // Refresh collection after direct exclude (Not for me)
  const handleExcludeGame = useCallback(async () => {
    if (!user) return;
    try {
      const res = await httpRequest(`${API_BASE}/profile/collection`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const games = Array.isArray(data) ? data : (data.games || []);
        setCollection(games.map(g => ({ ...g, id: g.id || g.game_id })));
      }
    } catch (err) {
      logger.error("[Explore] Failed to refresh collection after exclude:", err);
    }
  }, [user]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce(async (query) => {
      if (!query || query.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const res = await httpRequest(`${API_BASE}/games/search?q=${encodeURIComponent(query.trim())}&limit=15`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        });

        // Ignore responses for queries that are no longer current
        if (query.trim() !== latestSearchQueryRef.current.trim()) {
          return;
        }

        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.games || []);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        logger.error("Search failed:", err);
        setSearchResults([]);
      } finally {
        if (query.trim() === latestSearchQueryRef.current.trim()) {
          setIsSearching(false);
        }
      }
    }, 300),
    []
  );

  // Handle search query changes
  useEffect(() => {
    latestSearchQueryRef.current = searchQuery;
    if (searchQuery) {
      debouncedSearch(searchQuery);
      setIsDropdownVisible(true);
    } else {
      setSearchResults([]);
      setIsDropdownVisible(false);
      setIsSearching(false);
    }
  }, [searchQuery, debouncedSearch]);

  // Game selection handler
  const handleGameSelect = useCallback((game) => {
    trackClick('explore_search_select', { query: searchQuery, game_id: game.id || game.game_id, game_name: game.name });
    setSelectedGame(game);
    setSearchQuery('');
    setSearchResults([]);
    setIsDropdownVisible(false);
    if (user?.id) {
      writeLastExploreGame(user.id, game).catch((err) =>
        logger.warn("[Explore] failed to persist last game:", err)
      );
    }
  }, [user, trackClick, searchQuery]);

  // Handle "Open in Explore" navigation state from other pages
  useEffect(() => {
    const exploreGame = location.state?.exploreGame;
    if (!exploreGame) return;
    const key = `${exploreGame.id || exploreGame.game_id}_${location.key}`;
    if (lastHandledExploreKey.current === key) return;
    lastHandledExploreKey.current = key;
    handleGameSelect(exploreGame);
    if (window.history?.replaceState) {
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location, handleGameSelect]);

  // Handle /explore/:gameId deep-link parameter
  useEffect(() => {
    if (!gameId || fetchedGameIdParam.current === gameId) return;
    const numericGameId = parseInt(gameId, 10);
    if (Number.isNaN(numericGameId)) return;

    let cancelled = false;
    fetchedGameIdParam.current = gameId;

    (async () => {
      try {
        const res = await httpRequest(`${API_BASE}/games/${numericGameId}/details`, {
          method: 'GET',
          headers: authService.getAuthHeaders(),
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          const loadedGame = { ...data, id: data.id ?? data.game_id, game_id: data.game_id ?? data.id };
          handleGameSelect(loadedGame);
        }
      } catch (err) {
        if (!cancelled) {
          logger.warn('[Explore] failed to load game from URL param:', err);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [gameId, handleGameSelect]);

  // Close curate sidebar
  const handleCloseCurate = useCallback(() => {
    setShowCurateSidebar(false);
    setCurateMode(null);
    setBaseGame(null);
  }, []);

  // Clear search UI; restore default tile from preload (last-saved pick still wins on next mount)
  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsDropdownVisible(false);
    try {
      if (user?.id) {
        const saved = await readLastExploreGame(user.id);
        if (saved && ((saved.id !== null && saved.id !== undefined) || (saved.game_id !== null && saved.game_id !== undefined))) {
          const gid = saved.id ?? saved.game_id;
          setSelectedGame({ ...saved, id: gid, game_id: gid });
          return;
        }
      }
      const res = await httpRequest(`${API_BASE}/games/${DEFAULT_SAMPLE_GAME_ID}/details`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });
      if (res.ok) {
        const g = await res.json();
        const gid = g.id ?? g.game_id ?? DEFAULT_SAMPLE_GAME_ID;
        setSelectedGame({ ...g, id: gid, game_id: gid });
        return;
      }
      setSelectedGame(null);
    } catch (err) {
      if (err?.status === 404) {
        logger.warn("[Explore] sample game not found, skipping restore:", err?.message || err);
      } else {
        logger.error("[Explore] clear + preload restore failed:", err);
      }
      setSelectedGame(null);
    }
  }, [user]);

  const handleJetSetGoCardClick = useCallback(() => {
    trackClick("explore_landing_card_click", { card: "jet_set_go" });
    navigate("/quick-decision");
  }, [navigate, trackClick]);

  const handleCollectionCardClick = useCallback(() => {
    trackClick("explore_landing_card_click", { card: "collection" });
    if (user) {
      navigate("/collection");
    } else {
      navigate("/login?register=true");
    }
  }, [navigate, trackClick, user]);


  const searchPlaceholder = useMemo(() => {
    const gameName = selectedGame?.name?.trim();
    if (gameName) {
      return `Search for any board game (see ${gameName} below)`;
    }
    return "Search for any board game…";
  }, [selectedGame?.name]);

  return (
    <div className="explore-container explore-landing-backdrop">
      {playListBanner.show && (
        <div
          style={{
            position: "fixed",
            top: "calc(80px + env(safe-area-inset-top, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "12px 20px",
            borderRadius: "8px",
            backgroundColor: 'var(--success-color, #4caf50)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            animation: "slideInTop 0.3s ease-out",
            maxWidth: '400px',
            wordWrap: 'break-word',
            textAlign: 'center',
          }}
        >
          {playListBanner.message}
        </div>
      )}
      <div
        style={{
          paddingTop: "0rem",
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          position: "relative",
        }}
      >
      {/* Hero Section */}
      <div className="explore-hero-section">
        <div className="explore-hero-section__left">
          <div className="explore-hero-section__brand">
            <span className="explore-hero-section__brand-title">Pista</span>
            {process.env.REACT_APP_ENV === 'development' && (
              <span className="explore-hero-section__test-badge">TEST</span>
            )}
            <span className="explore-hero-section__brand-subtitle">by VoyagerBug</span>
          </div>
          <div className="explore-hero-section__text">
            <p className="explore-hero-section__subtitle">
              <span className="explore-hero-section__subtitle-any">Any</span>{' '}
              <span className="explore-hero-section__subtitle-keyword">group</span>,{' '}
              <span className="explore-hero-section__subtitle-any">any</span>{' '}
              <span className="explore-hero-section__subtitle-keyword">mood</span>,{' '}
              <span className="explore-hero-section__subtitle-any">any</span>{' '}
              <span className="explore-hero-section__subtitle-keyword">time</span>
            </p>
          </div>
        </div>
        {user && (
          <div className="explore-hero-section__activity">
            <div className="explore-hero-section__badge">
              <span className="explore-hero-section__greeting">
                Hello, <span className="explore-hero-section__username">{user.username || user.email}</span>!
              </span>
              <span className="explore-hero-section__last-game">
                Last looked at {lastActivity?.game_name || "—"}
              </span>
            </div>
          </div>
        )}
        {/* Get App CTA - mobile only (hidden on desktop via CSS) */}
        {installPrompts.enabled && installPrompts.googlePlayUrl && !Capacitor.isNativePlatform() && isAndroid() && !isInstalled() && (
          <a
            href={installPrompts.googlePlayUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="explore-hero-section__get-app"
          >
            <Smartphone size={16} strokeWidth={2.5} />
            Get the app
          </a>
        )}
      </div>

{/*         {user && (
          <div style={{ margin: "0", padding: "0 2rem" }}>
            <InsightCarousel compact variant="explore" />
          </div>
        )} */}
      </div>

      {isFirefox() && (
        <ShareWarningBanner
          message="Some features (e.g. sharing) may be limited in Firefox due to stricter privacy settings."
          className="explore-share-warning"
        />
      )}

      <section className="explore-landing-cards" aria-label="Explore features">
        <ExploreLandingCard
          icon={JET_SET_GO_ICON}
          title="Jet Set Go"
          description="Find the perfect match"
          offerPills={["Play Style", "Energy", "Mood"]}
          pills={user ? [] : ["No account needed"]}
          ctaLabel="Start playing"
          ctaTone="primary"
          onCardClick={handleJetSetGoCardClick}
          ariaLabel="Jet Set Go — start playing in 2 minutes"
        />
        <ExploreLandingCard
          icon={LIBRARY_ICON}
          title="Play Library"
          description={user ? "Organize, share, see insights" : "Create in 2 minutes"}
          offerPills={["I have this", "Interested", "Excluded"]}
          pills={user ? [] : ["Free account"]}
          ctaLabel={user ? (collection.length > 0 ? `View ${collection.length} games` : "View") : "Sign up - Quick Start"}
          ctaTone="primary"
          onCardClick={handleCollectionCardClick}
          ariaLabel="Play Library — organize, share, and browse your collection"
        />
      </section>

      <div className="explore-divider" role="separator" aria-label="Explore">
        <span className="explore-divider__line" aria-hidden="true" />
        <span className="explore-divider__text">Explore</span>
        <span className="explore-divider__line" aria-hidden="true" />
      </div>

      {/* Search Section */}
      <div className="explore-search-section">
        <div className="explore-search-row">
          <div className="explore-search-container">
            <GameSearchDropdown
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isVisible={isDropdownVisible}
              onVisibilityChange={setIsDropdownVisible}
              searchResults={searchResults}
              isLoading={isSearching}
              onGameSelect={handleGameSelect}
              showImages={true}
              placeholder={searchPlaceholder}
              autoFocus={true}
              trailingSlot={
                searchQuery.trim() || selectedGame ? (
                  <button
                    type="button"
                    className="game-search-trailing-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleClearSearch();
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label="Clear search"
                  >
                    {CLOSE_ICON}
                  </button>
                ) : (
                  <span className="game-search-trailing-icon" aria-hidden="true">
                    <Search size={18} />
                  </span>
                )
              }
            />
          </div>
          <button
            type="button"
            onClick={() => {
              trackClick('explore_open_play_list', { count: playListCount });
              setShowPlayListSidebar(true);
            }}
            className="explore-search-playlist-button"
            aria-label={!user ? 'Login to use Play List' : playListCount === 0 ? 'Play List is empty' : 'Open Play List'}
            title={!user ? 'Login to use Play List' : playListCount === 0 ? 'Play List is empty' : 'Open Play List'}
            disabled={!user || playListCount === 0}
          >
            <List size={20} />
            {user && playListCount > 0 && (
              <span className="explore-search-playlist-button__count">{playListCount}</span>
            )}
          </button>
        </div>
      </div>
      {/* Game Display Section */}
      {selectedGame && (
        <div className="explore-game-section">
          <div
            className="explore-game-container"
            data-game-id={selectedGame.game_id || selectedGame.id}
            data-game-name={selectedGame.name}
          >
            <GameTile
              key={selectedGame.game_id || selectedGame.id}
              game={selectedGame}
              user={user}
              setPreviewGame={setPreviewGame}
              showAddToCollection={true}
              isInCollection={!!getGameOwnershipStatus(selectedGame.game_id || selectedGame.id)}
              getGameOwnershipStatus={getGameOwnershipStatus}
              onAddToCollection={handleAddToCollection}
              getGameAvailability={getGameAvailability}
              onSearchSimilar={handleSearchSimilar}
              onAddToMarketplace={handleAddToMarketplace}
              onAddReminder={handleAddReminder}
              onLetSPlay={handleLetSPlay}
              onCompareGames={handleCompare}
              onTogglePlayList={handleTogglePlayList}
              inPlayList={isInPlayList(selectedGame.game_id || selectedGame.id)}
              hideMarketplaceButton={false}
              hideSimilarity={true}
              hideFeedback={true}
              gameChips={[]}
              onAddToSearch={null}
              onCompare={null}
              onExcludeGame={handleExcludeGame}
              hideOpenInExplore={true}
            />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedGame && !searchQuery && (
        <div className="explore-empty-state">
          <div className="explore-empty-content">
            <h2>Start Exploring</h2>
            <p>Search for any board game to see detailed information, similar games, and more.</p>
            <div className="explore-empty-icon">🔍</div>
          </div>
        </div>
      )}

      {/* Curate Sidebar */}
      {showCurateSidebar && (
        <CurateSimplified
          ref={curateRef}
          user={user}
          onClose={handleCloseCurate}
          sidebarMode={true}
          initialMode={curateMode}
          baseGame={baseGame}
          getGameOwnershipStatus={getGameOwnershipStatus}
        />
      )}

      {/* Play List Sidebar */}
      <PlayListSidebar
        isOpen={showPlayListSidebar}
        onClose={() => setShowPlayListSidebar(false)}
        playList={playList}
        loading={playListLoading}
        onRemove={removeFromPlayList}
        onClear={clearPlayList}
        onLaunchJetSetGo={() => navigate('/quick-decision')}
      />

      {/* Game Preview Modal */}
      <GamePreviewModal
        game={previewGame}
        onClose={() => setPreviewGame(null)}
        onSearchSimilar={handleSearchSimilar}
        onAddToMarketplace={handleAddToMarketplace}
        onAddWatch={null}
        onAddReminder={handleAddReminder}
        onLetSPlay={handleLetSPlay}
        letsPlayConfig={{
          onLogSession: (game) => {
            letSPlayHandlers.openModal(game);
            setShowScorePadInLetSPlay(false);
            setShowSessionForm(true);
          },
          onOpenScorePad: (game) => {
            letSPlayHandlers.openModal(game);
            setShowSessionForm(false);
            setShowScorePadInLetSPlay(true);
          },
        }}
        onCompareGames={handleCompare}
        user={user}
        onTogglePlayList={handleTogglePlayList}
        inPlayList={isInPlayList(previewGame?.game_id || previewGame?.id)}
      />

      {/* Add Game Modal */}
      {addGameModal && (
        <AddGameModal
          isOpen={true}
          game={addGameModal}
          user={user}
          onClose={() => setAddGameModal(null)}
          onSave={handleAddGameModalSuccess}
          onLaunchSession={handleLaunchSessionFromAddGame}
        />
      )}

      {/* Marketplace Modal */}
      {marketplaceGame && (
        <Marketplace
          gameId={marketplaceGame.id || marketplaceGame.game_id}
          gameName={marketplaceGame.name || marketplaceGame.game_name}
          onClose={() => setMarketplaceGame(null)}
        />
      )}

      {/* Reminder Modal */}
      {reminderGame && (
        <SimplifyReminderModal
          game={reminderGame}
          onClose={() => setReminderGame(null)}
          user={user}
        />
      )}

      {/* Let's Play Modal */}
      <LetsPlayModal
        isOpen={showLetSPlayModal}
        game={letSPlayGame}
        user={user}
        onClose={() => {
          setShowScorePadInLetSPlay(false);
          setScorePadSessionData(null);
          letSPlayHandlers.closeModal();
        }}
        showSessionForm={showSessionFormInLetSPlay}
        showRulesSummary={showRulesSummaryInLetSPlay}
        showScorePad={showScorePadInLetSPlay}
        onLogSession={letSPlayHandlers.logSession}
        onSeeRulesSummary={letSPlayHandlers.showRules}
        onOpenScorePad={handleOpenScorePad}
        onCloseScorePad={handleCloseScorePad}
        onLogSessionFromScorePad={handleLogSessionFromScorePad}
        scorePadSessionData={scorePadSessionData}
        onSaveSession={letSPlayHandlers.handleSessionLogged}
        showBanner={() => {}}
        userMap={userMap}
        source="explore"
      />
    </div>
  );
}

export default Explore;
